import axios, { AxiosInstance, AxiosError } from 'axios';
import type {
  XeroConnection,
  HighLevelConnection,
  SyncConfiguration,
  UpdateSyncConfiguration,
  SyncJob,
  EntityMapping,
  SyncStats,
  ApiError,
  SyncStatus,
  ManualSyncResponse,
  SyncEntityType,
  SyncDirection,
  SyncHistoryEntry,
} from '@/types';
import { getHighLevelContext } from '@/utils/highlevel';
import { getApiUrl } from '@/config/runtime';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: getApiUrl(),
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use((config) => {
      const context = getHighLevelContext();
      if (context?.authToken) {
        config.headers.Authorization = `Bearer ${context.authToken}`;
      }
      if (context?.locationId) {
        config.headers['X-Location-ID'] = context.locationId;
      }
      return config;
    });

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiError>) => {
        const apiError: ApiError = {
          message: error.response?.data?.message || error.message,
          code: error.response?.data?.code || error.code,
          status: error.response?.status,
          details: error.response?.data?.details,
        };
        return Promise.reject(apiError);
      }
    );
  }

  // OAuth endpoints
  async getXeroAuthUrl(locationId: string): Promise<string> {
    // Return direct authorization URL (backend will redirect to Xero)
    // API_URL should be the full API base URL (e.g., https://api.xero-dev.datasentry.com.au)
    const baseUrl = getApiUrl();
    return `${baseUrl}/auth/xero?locationId=${locationId}`;
  }

  async getXeroConnection(locationId: string): Promise<XeroConnection | null> {
    try {
      const { data } = await this.client.get(`/api/credentials`, {
        params: { locationId, provider: 'xero', isActive: 'true' },
      });
      // API returns { credentials: [...], total: number }
      const credentials = data.credentials || data;
      // Transform credentials response to XeroConnection format
      if (credentials && credentials.length > 0) {
        const cred = credentials[0];
        return {
          id: cred.id,
          credentialId: cred.id,
          tenantId: cred.xeroTenantId || '',
          tenantName: cred.xeroTenantName || 'Unknown Organization',
          tenantType: cred.xeroTenantType || 'ORGANISATION',
          isConnected: cred.isActive,
          createdAt: cred.createdAt,
          updatedAt: cred.updatedAt,
          expiresAt: cred.expiresAt,
        };
      }
      return null;
    } catch (error) {
      if ((error as ApiError).status === 404) {
        return null;
      }
      throw error;
    }
  }

  async disconnectXero(locationId: string, credentialId?: string): Promise<void> {
    if (!credentialId) {
      // Get the credential ID first
      const connection = await this.getXeroConnection(locationId);
      if (!connection?.credentialId) {
        throw new Error('No Xero connection found');
      }
      credentialId = connection.credentialId;
    }
    await this.client.delete(`/api/credentials/${credentialId}`);
  }

  // HighLevel OAuth endpoints (white-label compliant paths)
  async getHighLevelAuthUrl(locationId: string): Promise<string> {
    // Return direct authorization URL (backend will redirect)
    // API_URL should be the full API base URL (e.g., https://api.xero-dev.datasentry.com.au)
    const baseUrl = getApiUrl();
    return `${baseUrl}/auth/oauth?locationId=${locationId}`;
  }

  async getHighLevelConnection(locationId: string): Promise<HighLevelConnection | null> {
    try {
      // Auth routes are at /auth, not /api/auth, so construct URL without /api prefix
      const baseUrl = getApiUrl().replace(/\/api$/, '');
      const { data } = await axios.get(`${baseUrl}/auth/oauth/status/${locationId}`, {
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response?.status === 404) {
        return null;
      }
      throw {
        message: axiosError.response?.data?.message || axiosError.message,
        status: axiosError.response?.status,
      };
    }
  }

  async disconnectHighLevel(locationId: string): Promise<void> {
    // Auth routes are at /auth, not /api/auth, so construct URL without /api prefix
    const baseUrl = getApiUrl().replace(/\/api$/, '');
    await axios.delete(`${baseUrl}/auth/oauth/disconnect/${locationId}`, {
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // Sync configuration endpoints
  async getSyncConfiguration(
    locationId: string
  ): Promise<SyncConfiguration | null> {
    try {
      const { data } = await this.client.get(`/api/settings`, {
        params: { locationId },
      });
      return data;
    } catch (error) {
      if ((error as ApiError).status === 404) {
        return null;
      }
      throw error;
    }
  }

  async updateSyncConfiguration(
    locationId: string,
    config: UpdateSyncConfiguration
  ): Promise<SyncConfiguration> {
    const { data } = await this.client.put(`/api/settings`, config, {
      params: { locationId },
    });
    return data;
  }

  async createSyncConfiguration(
    locationId: string,
    config: UpdateSyncConfiguration
  ): Promise<SyncConfiguration> {
    // Backend uses PUT for both create and update (upsert pattern)
    const { data } = await this.client.put(`/api/settings`, config, {
      params: { locationId },
    });
    return data;
  }

  // Sync job endpoints
  async triggerSync(
    locationId: string,
    entityType?: 'contact' | 'product' | 'invoice'
  ): Promise<SyncJob> {
    const { data } = await this.client.post(`/api/sync/trigger`, {
      locationId,
      entityType,
    });
    return data;
  }

  async getSyncJobs(
    locationId: string,
    limit = 20,
    offset = 0
  ): Promise<{ jobs: SyncJob[]; total: number }> {
    // Use /api/sync/queue/:locationId which is the actual backend route
    try {
      const { data } = await this.client.get(`/api/sync/queue/${locationId}`, {
        params: { limit, offset },
      });
      // Transform queue items to SyncJob format
      return {
        jobs: data.items?.map((item: any) => ({
          id: item.id,
          entityType: item.entityType,
          status: item.status,
          direction: item.direction,
          createdAt: item.createdAt,
          scheduledAt: item.scheduledAt,
          errorMessage: item.errorMessage,
          retryCount: item.retryCount,
        })) || [],
        total: data.totalItems || 0,
      };
    } catch (error) {
      // Return empty result on error (e.g., table doesn't exist yet)
      if ((error as ApiError).status === 500 || (error as ApiError).status === 404) {
        return { jobs: [], total: 0 };
      }
      throw error;
    }
  }

  async getSyncJob(jobId: string): Promise<SyncJob> {
    const { data } = await this.client.get(`/api/sync/queue/${jobId}`);
    return data;
  }

  // Entity mapping endpoints (not yet implemented on backend)
  async getEntityMappings(
    _locationId: string,
    _entityType?: 'contact' | 'product' | 'invoice',
    _limit = 50,
    _offset = 0
  ): Promise<{ mappings: EntityMapping[]; total: number }> {
    // Entity mappings endpoint not implemented yet - return empty
    return { mappings: [], total: 0 };
  }

  async resolveConflict(
    mappingId: string,
    resolution: 'use_highlevel' | 'use_xero'
  ): Promise<EntityMapping> {
    const { data } = await this.client.post(
      `/api/sync/mappings/${mappingId}/resolve`,
      { resolution }
    );
    return data;
  }

  // Stats endpoint - uses /api/sync/status with query param
  async getSyncStats(locationId: string): Promise<SyncStats> {
    try {
      const { data } = await this.client.get(`/api/sync/status`, {
        params: { locationId },
      });
      // The backend returns SyncStatusResponse which contains stats-like data
      // Transform to SyncStats format
      return {
        totalContacts: data.syncState?.contacts?.syncedCount || 0,
        syncedContacts: data.syncState?.contacts?.syncedCount || 0,
        failedContacts: data.syncState?.contacts?.failedCount || 0,
        totalProducts: data.syncState?.products?.syncedCount || 0,
        syncedProducts: data.syncState?.products?.syncedCount || 0,
        failedProducts: data.syncState?.products?.failedCount || 0,
        totalInvoices: data.syncState?.invoices?.syncedCount || 0,
        syncedInvoices: data.syncState?.invoices?.syncedCount || 0,
        failedInvoices: data.syncState?.invoices?.failedCount || 0,
        lastSyncAt: data.syncState?.contacts?.lastSyncAt || undefined,
        nextSyncAt: undefined,
      };
    } catch (error) {
      // Return empty stats on error
      if ((error as ApiError).status === 500 || (error as ApiError).status === 404) {
        return {
          totalContacts: 0,
          syncedContacts: 0,
          failedContacts: 0,
          totalProducts: 0,
          syncedProducts: 0,
          failedProducts: 0,
          totalInvoices: 0,
          syncedInvoices: 0,
          failedInvoices: 0,
          lastSyncAt: undefined,
          nextSyncAt: undefined,
        };
      }
      throw error;
    }
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const { data } = await this.client.get('/health');
    return data;
  }

  // Generic GET method for custom endpoints
  async get<T = any>(url: string, params?: Record<string, string>): Promise<T> {
    const { data } = await this.client.get(url, { params });
    return data;
  }

  // Generic POST method for custom endpoints
  async post<T = any>(url: string, body?: any): Promise<T> {
    const { data } = await this.client.post(url, body);
    return data;
  }

  // Manual Sync API
  async triggerManualSync(
    locationId: string,
    entityType: SyncEntityType,
    direction: SyncDirection,
    fullSync: boolean = false
  ): Promise<ManualSyncResponse> {
    const { data } = await this.client.post(`/api/sync/${entityType}/manual`, {
      locationId,
      direction,
      fullSync,
    });
    return data;
  }

  async getSyncStatus(locationId: string): Promise<SyncStatus> {
    const { data } = await this.client.get(`/api/sync/status`, {
      params: { locationId },
    });
    return data;
  }

  async refreshEntityCounts(
    locationId: string,
    options?: {
      system?: 'highlevel' | 'xero';
      entityType?: 'contact' | 'product' | 'invoice';
      runContactMatching?: boolean;
    }
  ): Promise<{
    success: boolean;
    highlevel?: { contacts: number; products: number; invoices: number };
    xero?: { contacts: number; products: number; invoices: number };
    synced?: { contacts: number; products: number; invoices: number };
    entityType?: string;
    counts?: { highlevel: number; xero: number; synced: number };
    updatedAt: string;
  }> {
    const { data } = await this.client.post(`/api/sync/refresh-counts`, {
      locationId,
      system: options?.system,
      entityType: options?.entityType,
      runContactMatching: options?.runContactMatching,
    });
    return data;
  }

  /**
   * Run contact matching to create sync mappings for contacts that exist in both systems
   */
  async matchContacts(locationId: string): Promise<{
    success: boolean;
    matchedCount: number;
    newMappingsCreated: number;
    existingMappingsFound: number;
    message: string;
  }> {
    const { data } = await this.client.post(`/api/sync/match-contacts`, {
      locationId,
    });
    return data;
  }

  /**
   * Get a sync history entry by ID (for progress tracking)
   */
  async getSyncHistoryById(syncHistoryId: string): Promise<SyncHistoryEntry | null> {
    try {
      const { data } = await this.client.get(`/api/sync/history/${syncHistoryId}`);
      return data;
    } catch (error) {
      if ((error as ApiError).status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get recent sync history for a location
   */
  async getSyncHistory(
    locationId: string,
    options?: {
      limit?: number;
      entityType?: string;
      syncType?: string;
      status?: string;
    }
  ): Promise<{ items: SyncHistoryEntry[]; total: number }> {
    try {
      const { data } = await this.client.get(`/api/sync/history`, {
        params: { locationId, ...options },
      });
      return data;
    } catch (error) {
      // Return empty if table doesn't exist or other errors
      if ((error as ApiError).status === 500 || (error as ApiError).status === 404) {
        return { items: [], total: 0 };
      }
      throw error;
    }
  }

  /**
   * Uninstall the plugin for a location.
   * Archives all data with a 30-day retention period before permanent deletion.
   */
  async uninstallPlugin(
    locationId: string,
    confirmationText: string
  ): Promise<{
    success: boolean;
    locationId: string;
    archivedRowCounts: Record<string, number>;
    credentialsDeactivated: number;
    queueItemsCancelled: number;
    cascadeDeletesCancelled: number;
    retentionDays: number;
    hardDeleteAfter: string;
    uninstallLogId: string;
  }> {
    const { data } = await this.client.post('/api/uninstall', {
      locationId,
      confirmationText,
    });
    return data;
  }

  /**
   * Check if a location has been uninstalled.
   */
  async getUninstallStatus(locationId: string): Promise<{ isUninstalled: boolean }> {
    const { data } = await this.client.get('/api/uninstall/status', {
      params: { locationId },
    });
    return data;
  }
}

export const apiClient = new ApiClient();
